import { AtoB, replaceUrl } from '../../utils/UrlUtils'
import React, { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { AccessRoleConstants } from '../../constants/AccessRoleConstants'
import PageLoadingOverlay from '../../components/common/PageLoadingOverlay/PageLoadingOverlay'
import SecureComponent from '../../components/Layout/SecureComponent'
import StringConstants from '../../constants/StringConstants'
import { ToastMessageConstants } from '../../constants/ToastMessageConstants'
import ToastNotification from '../../components/common/DDS/Toast/Toast'
import ValidationConstants from '../../constants/ValidationConstants'
import { connectToSalesforceInstance } from '../../api/salesforceAudit/salesforceAudit.service'
import { observer } from 'mobx-react-lite'
import routeConstants from '../../constants/RouteConstants'
import { useStoreContext } from '../../store/StoreContext'

interface SalesforceState {
  organizationId: string
  projectId: string
  environmentId: string
  accountType?: 'existing' | 'new'
}

const SalesforceConnect = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const store = useStoreContext()
  const { salesforceAuditStore } = store

  const searchParams = new URLSearchParams(location.search)
  const code = searchParams.get('code')
  const state = searchParams.get('state')
  const error = searchParams.get('error')

  useEffect(() => {
    const headers = safeParseState(state)

    if (!headers) {
      ToastNotification({
        type: 'error',
        message: 'Invalid Salesforce callback URL.'
      })
      navigate(routeConstants.HOME)
      return
    }

    const { organizationId, projectId, environmentId, accountType } = headers

    if (error === 'access_denied') {
      ToastNotification({
        type: 'error',
        message:
          accountType === 'existing'
            ? 'Salesforce reconnection denied.'
            : 'Salesforce connection failed. Please try again.'
      })

      if (organizationId && projectId && environmentId) {
        navigate(
          replaceUrl(
            routeConstants.SALESFORCE_AUDIT_DASHBOARD,
            organizationId,
            projectId,
            environmentId
          )
        )
      } else {
        navigate(routeConstants.HOME)
      }
      return
    }

    // Missing required params
    if (!code || !state || !organizationId || !projectId || !environmentId) {
      ToastNotification({
        type: 'error',
        message: 'Missing or invalid Salesforce connection data.'
      })
      navigate(routeConstants.HOME)
      return
    }

    connectToSalesforceInstance(decodeURIComponent(code), headers)
      .then((response: any) => {
        ToastNotification({
          type: 'success',
          message:
            ToastMessageConstants.SALESFORCE_AUDIT.CONNECT_TO_SALESFORCE.SUCCESS
        })
        salesforceAuditStore.setAuditData(response?.data?.auditData)
        navigate(
          replaceUrl(
            routeConstants.SALESFORCE_AUDIT_DASHBOARD,
            organizationId,
            projectId,
            environmentId
          )
        )
      })
      .catch((error: any) => {
        if (error === StringConstants.INSTANCE_ALREADY_CONNECTED) {
          ToastNotification({
            type: 'error',
            message:
              ValidationConstants.CONNECT_SALESFORCE_ORGANIZATION
                .INSTANCE_UN_AVAILABLE
          })
        } else if (error === StringConstants.INCORRECT_INSTANCE_URL) {
          ToastNotification({
            type: 'error',
            message:
              ValidationConstants.CONNECT_SALESFORCE_ORGANIZATION
                .INVALID_RECONNECTION_INSTANCE_URL
          })
        } else {
          ToastNotification({
            type: 'error',
            message:
              ToastMessageConstants.SALESFORCE_AUDIT.CONNECT_TO_SALESFORCE.ERROR
          })
        }
        navigate(
          replaceUrl(
            routeConstants.SALESFORCE_AUDIT_DASHBOARD,
            organizationId,
            projectId,
            environmentId
          )
        )
      })
  }, [])

  return (
    <PageLoadingOverlay loadingTitle='Please do not hit refresh button while we fetch data from Salesforce' />
  )
}

const safeParseState = (
  encodedState: string | null
): SalesforceState | null => {
  if (!encodedState) return null
  try {
    return JSON.parse(AtoB(encodedState))
  } catch (e) {
    console.error('Failed to decode state:', e)
    return null
  }
}

export default SecureComponent(
  observer(SalesforceConnect),
  AccessRoleConstants.MEMBER_LEVEL
)
